/**
 * Create Company Modal
 *
 * Reusable modal for creating new companies with Bolagsverket lookup
 */

import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  X,
  AlertCircle,
  Loader2,
  CheckCircle,
  Search,
  Building2,
  MapPin,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { companiesApi, type Company } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";

const createCompanySchema = z.object({
  organizationNumber: z
    .string()
    .min(10, "Organisationsnummer måste vara minst 10 siffror")
    .max(13, "Ogiltigt format"),
  name: z.string().min(1, "Företagsnamn krävs"),
  type: z.enum(["AB", "EF", "HB", "KB"]).default("AB"),
  seatMunicipality: z.string().optional(),
  address: z.string().min(1, "Adress krävs").max(255),
  postalCode: z.string().min(1, "Postnummer krävs").max(10),
  city: z.string().min(1, "Stad krävs").max(100),
});

type CreateCompanyData = z.infer<typeof createCompanySchema>;

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanyCreated: (company: Company) => void;
}

export function CreateCompanyModal({
  isOpen,
  onClose,
  onCompanyCreated,
}: CreateCompanyModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Bolagsverket lookup state
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupComplete, setLookupComplete] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateCompanyData>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      type: "AB",
    },
  });

  const formUnlocked = lookupComplete || lookupFailed;

  const handleCreateCompany = async (data: CreateCompanyData) => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await companiesApi.create(data);

      if (response.success && response.data) {
        toast.success("Företaget har lagts till");
        onCompanyCreated(response.data.company);
        handleClose();
      } else {
        setCreateError(response.error?.message || "Kunde inte skapa företag");
      }
    } catch (err) {
      console.error("Create company error:", err);
      toast.error("Kunde inte skapa företag");
      setCreateError("Ett fel uppstod");
    } finally {
      setIsCreating(false);
    }
  };

  // Lookup company from Bolagsverket
  const handleLookupCompany = useCallback(
    async (orgNumber: string) => {
      // Clean and validate org number
      const digits = orgNumber.replace(/\D/g, "");
      if (digits.length < 10) return;

      setIsLookingUp(true);
      setLookupError(null);
      setLookupFailed(false);

      try {
        const response = await companiesApi.lookup(orgNumber);

        if (response.success && response.data) {
          // Populate form with lookup data
          setValue("name", response.data.name);
          setValue("type", response.data.type);
          if (response.data.seatMunicipality) {
            setValue("seatMunicipality", response.data.seatMunicipality);
          }
          if (response.data.address) {
            if (response.data.address.street) {
              setValue("address", response.data.address.street);
            }
            if (response.data.address.postalCode) {
              setValue("postalCode", response.data.address.postalCode);
            }
            if (response.data.address.city) {
              setValue("city", response.data.address.city);
            }
          }
          setLookupComplete(true);
          setLookupFailed(false);
        } else {
          setLookupFailed(true);
          setLookupError(response.error?.message || "Företaget hittades inte");
        }
      } catch (err) {
        console.error("Lookup error:", err);
        toast.error("Kunde inte ansluta till Bolagsverket");
        setLookupFailed(true);
        setLookupError("Kunde inte ansluta till Bolagsverket");
      } finally {
        setIsLookingUp(false);
      }
    },
    [setValue]
  );

  // Reset lookup state
  const resetLookupState = () => {
    setLookupComplete(false);
    setLookupFailed(false);
    setLookupError(null);
    setIsLookingUp(false);
  };

  const handleClose = () => {
    setCreateError(null);
    reset();
    resetLookupState();
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Skapa nytt företag
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleCreateCompany)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <AlertCircle
                  className="text-red-600 mt-0.5 flex-shrink-0"
                  size={16}
                />
                <p className="text-sm text-red-600">{createError}</p>
              </div>
            )}

            {/* Section: Företagsinformation */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900">
                  Företagsinformation
                </h3>
              </div>

              {/* Organisationsnummer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Organisationsnummer
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Hash size={15} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register("organizationNumber")}
                      className="input pl-9"
                      placeholder="XXXXXX-XXXX"
                      disabled={isLookingUp}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      const orgNumber = watch("organizationNumber");
                      if (orgNumber) {
                        handleLookupCompany(orgNumber);
                      }
                    }}
                    disabled={
                      isLookingUp ||
                      !watch("organizationNumber") ||
                      watch("organizationNumber").replace(/\D/g, "").length < 10
                    }
                  >
                    {isLookingUp ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <Search size={15} />
                        Hämta uppgifter
                      </>
                    )}
                  </Button>
                </div>
                {errors.organizationNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.organizationNumber.message}
                  </p>
                )}
                {lookupComplete && (
                  <p className="text-sm text-green-600 mt-1.5 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Uppgifter hämtade från Bolagsverket
                  </p>
                )}
                {lookupError && (
                  <p className="text-sm text-amber-600 mt-1.5">
                    {lookupError}. Du kan fylla i uppgifterna manuellt.
                  </p>
                )}
                {!formUnlocked && !errors.organizationNumber && (
                  <p className="text-sm text-gray-500 mt-1.5">
                    Ange organisationsnummer och klicka &quot;Hämta uppgifter&quot; för att låsa upp formuläret
                  </p>
                )}
              </div>

              {/* Företagsnamn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Företagsnamn *
                </label>
                <input
                  type="text"
                  {...register("name")}
                  className="input"
                  placeholder="Företaget AB"
                  disabled={!formUnlocked}
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>

            {/* Section: Adressuppgifter */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900">
                  Adressuppgifter
                </h3>
              </div>

              {/* Adress */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Adress *
                </label>
                <input
                  type="text"
                  {...register("address")}
                  className="input"
                  placeholder="Gatuadress"
                  disabled={!formUnlocked}
                />
                {errors.address && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.address.message}
                  </p>
                )}
              </div>

              {/* Postnummer + Stad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Postnummer *
                  </label>
                  <input
                    type="text"
                    {...register("postalCode")}
                    className="input"
                    placeholder="123 45"
                    disabled={!formUnlocked}
                  />
                  {errors.postalCode && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.postalCode.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Stad *
                  </label>
                  <input
                    type="text"
                    {...register("city")}
                    className="input"
                    placeholder="Stockholm"
                    disabled={!formUnlocked}
                  />
                  {errors.city && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.city.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Län (seatMunicipality) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Län (valfritt)
                </label>
                <input
                  type="text"
                  {...register("seatMunicipality")}
                  className="input"
                  placeholder="Stockholms län"
                  disabled={!formUnlocked}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isCreating || !formUnlocked}
            >
              {isCreating ? <Spinner size="sm" /> : "Skapa företag"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
